import { useState, useRef, useEffect, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Heart, RotateCcw, Sparkles, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { UserProfile } from "@/hooks/useUsers";
import SwipeCard from "./SwipeCard";

interface SwipeStackProps {
  users: UserProfile[];
  onSwipeLeft: (user: UserProfile) => void;
  onSwipeRight: (user: UserProfile, isSuperLike?: boolean) => void;
  onUndo?: (user: UserProfile) => void;
  onEmpty?: () => void;
  calculateMatchPercentage?: (user: UserProfile) => number;
  canUndo?: boolean;
}

const SwipeStack = ({ 
  users, 
  onSwipeLeft, 
  onSwipeRight, 
  onUndo,
  onEmpty, 
  calculateMatchPercentage,
  canUndo = false
}: SwipeStackProps) => {
  const [localSwipedIds, setLocalSwipedIds] = useState<Set<string>>(new Set());
  const [swipeHistory, setSwipeHistory] = useState<{ user: UserProfile; direction: "left" | "right" | "super" }[]>([]);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showSuperLikeEffect, setShowSuperLikeEffect] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // Filter out locally swiped users immediately for instant UI feedback
  const availableUsers = useMemo(() => {
    return users.filter(u => !localSwipedIds.has(u.id));
  }, [users, localSwipedIds]);

  // Show the first 3 users from the available users
  const currentUsers = availableUsers.slice(0, 3);
  const currentUser = currentUsers[0];

  // Check if we've reached the end
  useEffect(() => {
    if (availableUsers.length === 0 && onEmpty) {
      onEmpty();
    }
  }, [availableUsers.length, onEmpty]);

  const handleSwipe = (direction: "left" | "right" | "super") => {
    if (!currentUser || isAnimating) return;

    setIsAnimating(true);
    
    // Immediately add to local swiped set for instant UI update
    setLocalSwipedIds(prev => new Set(prev).add(currentUser.id));
    setSwipeHistory(prev => [...prev, { user: currentUser, direction }]);

    if (direction === "left") {
      onSwipeLeft(currentUser);
    } else if (direction === "super") {
      setShowSuperLikeEffect(true);
      setTimeout(() => setShowSuperLikeEffect(false), 1000);
      onSwipeRight(currentUser, true);
    } else {
      onSwipeRight(currentUser, false);
    }

    // Reset animating state after a short delay
    setTimeout(() => {
      setIsAnimating(false);
    }, 300);
  };

  const handleUndo = () => {
    if (swipeHistory.length === 0) return;
    
    const lastSwiped = swipeHistory[swipeHistory.length - 1];
    
    // Remove from local swiped set
    setLocalSwipedIds(prev => {
      const next = new Set(prev);
      next.delete(lastSwiped.user.id);
      return next;
    });
    setSwipeHistory(prev => prev.slice(0, -1));
    
    if (onUndo) {
      onUndo(lastSwiped.user);
    }
  };

  const triggerSwipe = (direction: "left" | "right" | "super") => {
    if (isAnimating) return;
    handleSwipe(direction);
  };

  // Return null when empty - let parent handle the empty state
  if (currentUsers.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col items-center relative">
      {/* Super Like Effect Overlay */}
      <AnimatePresence>
        {showSuperLikeEffect && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.5 }}
            className="absolute inset-0 z-50 flex items-center justify-center pointer-events-none"
          >
            <motion.div
              animate={{ 
                rotate: [0, 15, -15, 0],
                scale: [1, 1.2, 1]
              }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <Star className="h-32 w-32 text-blue-500 fill-blue-500 drop-shadow-lg" />
              <p className="text-2xl font-bold text-blue-500 mt-2">SUPER LIKE!</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Card Stack */}
      <div ref={cardRef} className="relative w-full h-[500px] max-w-sm mx-auto">
        <AnimatePresence mode="wait">
          {currentUsers.map((user, index) => (
            <SwipeCard
              key={user.id}
              user={user}
              onSwipe={(dir) => handleSwipe(dir)}
              isTop={index === 0 && !isAnimating}
              stackIndex={index}
              matchPercentage={calculateMatchPercentage ? calculateMatchPercentage(user) : 0}
            />
          ))}
        </AnimatePresence>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-center gap-3 mt-6">
        {/* Undo Button */}
        <Button
          variant="outline"
          size="icon"
          className="h-12 w-12 rounded-full border-2 shadow-soft transition-transform hover:scale-110"
          onClick={handleUndo}
          disabled={swipeHistory.length === 0 && !canUndo}
        >
          <RotateCcw className="h-5 w-5 text-muted-foreground" />
        </Button>

        {/* Nope Button */}
        <Button
          variant="outline"
          size="icon"
          className="h-16 w-16 rounded-full border-2 border-destructive hover:bg-destructive/10 shadow-strong transition-transform hover:scale-110"
          onClick={() => triggerSwipe("left")}
          disabled={isAnimating}
        >
          <X className="h-8 w-8 text-destructive" />
        </Button>

        {/* Super Like Button */}
        <Button
          variant="outline"
          size="icon"
          className="h-14 w-14 rounded-full border-2 border-blue-500 hover:bg-blue-500/10 shadow-strong transition-transform hover:scale-110"
          onClick={() => triggerSwipe("super")}
          disabled={isAnimating}
        >
          <Star className="h-6 w-6 text-blue-500 fill-blue-500" />
        </Button>

        {/* Like Button */}
        <Button
          size="icon"
          className="h-16 w-16 rounded-full bg-green-500 hover:bg-green-600 shadow-strong transition-transform hover:scale-110"
          onClick={() => triggerSwipe("right")}
          disabled={isAnimating}
        >
          <Heart className="h-8 w-8 text-white fill-white" />
        </Button>
      </div>

      {/* Progress indicator */}
      <div className="mt-4 text-sm text-muted-foreground">
        {swipeHistory.length + 1} / {users.length}
      </div>
    </div>
  );
};

export default SwipeStack;